/**
 * queueStoreWrite — LAYER 1 of the offline queue: the ONLY door that writes it.
 *
 * WHY (TASK-FE-OUTBOX-SERIALIZE-QUEUE-WRITES-2026-10-09): the outbox is ONE IndexedDB value
 * holding the WHOLE array, and every writer used to be its own unserialised read-modify-write.
 * Two that overlap read the same array, and the later write erases the earlier. The flush was
 * the worst of them — it read, awaited each handler over the network, then wrote its
 * START-OF-FLUSH snapshot back — so a field report enqueued during a flush was LOST from the
 * device, silently (pre-existing on prod v7).
 *
 * THE CURE: every write is a step on ONE in-module promise chain. A step reads the CURRENT
 * array inside the critical section, computes the next one, and writes it before the next step
 * may read. The raw writers are module-private, so no module can write around the chain; the
 * guarantee is per tab (cross-tab serialisation is an accepted, declared tradeoff of the plan).
 *
 * RULES FOR CALLERS, because the chain only protects what runs INSIDE it:
 *   - never await the network inside a step: it would stall every enqueue behind it;
 *   - never call `flushQueue` (or anything that updates the queue) inside a step: the step
 *     would wait on a step queued behind itself — a deadlock;
 *   - a step that finds its target entry ABSENT changes nothing: an update never resurrects.
 *
 * Imports only queueStore (reads, keys, listeners), so the dependency arrow stays one-way.
 */

import { set } from 'idb-keyval'
import {
  QUEUE_KEY,
  DISCARDED_PREDECESSORS_KEY,
  readQueue,
  readDiscardedPredecessors,
  emitQueueChange,
  olderPinnedTwins,
  type DiscardedPredecessor,
  type DeliveredWrite,
} from './queueStore'
import type { QueueEntry } from './offlineQueueTypes'

/**
 * What one step decided. `unchanged` is explicit — not an empty array, not a null — so a step
 * that changed nothing writes nothing and fires no change listener, exactly as the per-writer
 * "only write when something changed" guards did before.
 */
export type StoreUpdate<S, T> = { next: S; result: T } | { unchanged: true; result: T }

type Step<S, T> = (current: S) => StoreUpdate<S, T> | Promise<StoreUpdate<S, T>>

/**
 * One serialised read-modify-write chain per stored key. Each step runs AFTER every earlier one
 * has settled. A step's own result (or error) goes to ITS caller; the chain only waits for it,
 * so one failed step never blocks or fails the next.
 */
function serialChain(): <T>(run: () => Promise<T>) => Promise<T> {
  let tail: Promise<void> = Promise.resolve()
  return <T>(run: () => Promise<T>): Promise<T> => {
    const step = tail.then(run)
    tail = step.then(() => undefined, () => undefined)
    return step
  }
}

const onQueue = serialChain()
const onDiscardList = serialChain()

async function writeQueue(queue: QueueEntry[]): Promise<void> {
  await set(QUEUE_KEY, queue)
  emitQueueChange(queue.filter(e => e.status === 'pending').length)
}

async function writeDiscardedPredecessors(discarded: DiscardedPredecessor[]): Promise<void> {
  await set(DISCARDED_PREDECESSORS_KEY, discarded)
}

/** Serialised read-modify-write of the queue. `fn` sees the CURRENT array, never a snapshot. */
export function updateQueue<T>(fn: Step<QueueEntry[], T>): Promise<T> {
  return onQueue(async () => {
    const update = await fn(await readQueue())
    if (!('unchanged' in update)) await writeQueue(update.next)
    return update.result
  })
}

/** Serialised read-modify-write of the discarded-predecessors list (queueOrdering's fourth fate). */
export function updateDiscardedPredecessors<T>(fn: Step<DiscardedPredecessor[], T>): Promise<T> {
  return onDiscardList(async () => {
    const update = await fn(await readDiscardedPredecessors())
    if (!('unchanged' in update)) await writeDiscardedPredecessors(update.next)
    return update.result
  })
}

/**
 * Replace each entry of `current` that has an `outcomes` twin (same id) by that twin, keeping
 * `current`'s order and every entry without a twin untouched. An outcome whose entry is no
 * longer in `current` is DROPPED: it left the queue (synced on its owner's path, discarded)
 * while the outcome was being computed, and re-creating it would re-send or re-surface a write
 * the rep is already done with. Pure — the flush applies its per-entry results through this.
 */
export function applyOutcomesById(current: readonly QueueEntry[], outcomes: readonly QueueEntry[]): QueueEntry[] {
  const byId = new Map(outcomes.map(o => [o.id, o]))
  return current.map(e => {
    const outcome = byId.get(e.id)
    // No outcome for this entry = this cycle did not process it; it stays exactly as stored.
    return outcome === undefined ? e : outcome
  })
}

/** One entry a flush cycle changed: the status it had in the cycle's snapshot, and what the cycle made of it. */
export interface CycleOutcome { seen: QueueEntry['status']; entry: QueueEntry }

/**
 * The cycle's outcomes that may still be applied: those whose stored entry is in the SAME status the
 * cycle acted on. While a replay was on the network another writer may have DISPOSED of that very entry
 * — above all the report's own online attempt, refused for good, pinning it FINAL (durableSubmit 3b,
 * pinQueueEntryFinal below). The cycle's result for it is then stale: a transient replay failure
 * applied over the pin would turn a finally refused report back into `pending` and send it again.
 * The stored disposition wins; an entry that is gone stays gone (applyOutcomesById). Pure.
 */
export function outcomesStillDue(current: readonly QueueEntry[], changed: readonly CycleOutcome[]): QueueEntry[] {
  const storedStatus = new Map(current.map(e => [e.id, e.status]))
  return changed.filter(c => storedStatus.get(c.entry.id) === c.seen).map(c => c.entry)
}

/**
 * Remove ONE entry by id — the durable-outbox delete primitive.
 *
 * FR-FE-IRONCLAD-TELEMETRY-OUTBOX: `useOfflineMutation` persists a payload here
 * BEFORE the network attempt (persist-before-network), then removes exactly that
 * entry once the server has confirmed the write. Keyed on the minted `id` (not a
 * payload predicate like `dequeue`) so it removes the one entry it created and
 * nothing else — two rapid submits of the same body must not collapse into one.
 * A no-op when the id is already gone (a confirmed remove that raced a flush that
 * already pruned the synced sentinel is not an error — D-33 R5 verify-the-shape:
 * the post-condition is "id absent", which holds either way).
 */
export function removeQueueEntry(id: string): Promise<void> {
  return updateQueue(current => ({ next: current.filter(e => e.id !== id), result: undefined }))
}

/**
 * Pin ONE entry by id as a FINAL refusal — the online-submit twin of the drain path's
 * deterministic disposition (replayFailure.applyReplayFailure): status 'failed', retries
 * at maxRetries, failureReason 'non-retryable-4xx', no rescheduling. The entry is KEPT, so
 * it is a genuine failure (isGenuinelyFailed) the failed-entries surface shows, that no
 * flush re-sends (it is not `pending`) and no retry exit re-pends (isRetryableReason).
 *
 * `finalCode` (retryClassification.refusalCodeOf) is stored with it, so a surface can say WHY.
 *
 * Deliberately NO off-device capture: the online caller's door sink is the single capture
 * of an online refusal (durableSubmit re-throws to it). A second copy here would double it.
 *
 * A step on the write chain: the pin lands on the CURRENT queue, so a flush that is mid-cycle
 * cannot write an older copy of this entry over it (the flush only applies entries it changed).
 *
 * THROWS when the id is absent — the caller pins the entry it persisted moments ago, so a
 * missing row means the outbox lost it, and that must never read as "pinned" (D-33 R1).
 * Nothing is written in that case.
 */
export function pinQueueEntryFinal(id: string, finalCode: string): Promise<void> {
  return updateQueue(current => {
    if (!current.some(e => e.id === id)) {
      throw new Error(`[offlineQueue] cannot pin entry ${id} as final: it is not in the outbox`)
    }
    const next = current.map(e => {
      if (e.id !== id) return e
      const pinned: QueueEntry = { ...e, status: 'failed', retries: e.maxRetries, failureReason: 'non-retryable-4xx', finalCode }
      delete pinned.nextAttemptAt
      return pinned
    })
    return { next, result: undefined }
  })
}

/**
 * NEVER ZERO COPIES BEFORE CONFIRMED DELIVERY (coordinator ruling, signal 9e231b16). A pinned copy
 * of a write leaves the outbox ONLY once a newer attempt of the SAME write was DELIVERED — the
 * server confirmed it (2xx) or answered that it already stores it. Never on the newer attempt's
 * enqueue, and never on its failure: a newer attempt that fails retryably stays pending BESIDE the
 * pin, one that is refused for good is pinned BESIDE it — some copy of the rep's work exists at
 * every instant until the server holds it. Returns how many pins were resolved.
 *
 * The online submit's twin (durableSubmit, BEFORE the confirmed attempt's own entry is removed — its
 * position is what orders the twins). The twins are looked up on the CURRENT queue inside the step.
 */
export function removeDeliveredTwins(delivered: DeliveredWrite): Promise<number> {
  return updateQueue(current => {
    const twins = olderPinnedTwins(current, delivered)
    if (twins === null || twins.length === 0) return { unchanged: true, result: 0 }
    return { next: current.filter(e => !twins.includes(e)), result: twins.length }
  })
}
